/**
 * Console deep links Avery may put in a chat reply.
 *
 * One declaration shared by both halves:
 *   - the Worker's `get_console_link` tool builds every path with
 *     `buildConsoleLinkPath`, after resolving the record inside the caller's
 *     scope, so Avery never writes a URL herself;
 *   - the console's chat renderer turns `[label](path)` into a link ONLY when
 *     `isConsoleLinkPath(path)` accepts it. Anything else (an external URL, a
 *     `javascript:` scheme, a route not listed here, an unexpected param)
 *     renders as plain text.
 *
 * A link grants nothing. Every target screen still runs its own auth and scope
 * checks when it opens; this list only decides what may be clickable.
 *
 * The routes are the deep links the console already honours:
 *   /zones?zone=<id>&tab=…   Zones.tsx + zoneTabLinks.ts (events → Rates, scrolled to Event dates)
 *   /avery?proposal=<id>     screens/Avery.tsx
 *   /transactions?q=<text>   screens/Transactions.tsx
 *   /permits?permit=<id>     screens/Permits.tsx
 *   /validations?edit=<code> screens/Validations.tsx
 */

export type ConsoleLinkTarget =
  | 'zone'
  | 'zone_rates'
  | 'zone_events'
  | 'zone_hours'
  | 'zone_closures'
  | 'proposal'
  | 'transactions'
  | 'permit'
  | 'validation'

/** The console permission each target's screen needs (mirrors TOOL_PERMISSION). */
export const CONSOLE_LINK_PERMISSION: Record<ConsoleLinkTarget, string> = {
  zone: 'view.zones',
  zone_rates: 'view.zones',
  zone_events: 'view.zones',
  zone_hours: 'view.zones',
  zone_closures: 'view.zones',
  proposal: 'view.zones',
  transactions: 'view.transactions',
  permit: 'view.permits',
  validation: 'view.validations',
}

export const CONSOLE_LINK_TARGETS = Object.keys(CONSOLE_LINK_PERMISSION) as ConsoleLinkTarget[]

export function isConsoleLinkTarget(v: unknown): v is ConsoleLinkTarget {
  return typeof v === 'string' && Object.prototype.hasOwnProperty.call(CONSOLE_LINK_PERMISSION, v)
}

/**
 * Own-property lookup for a map keyed by untrusted text. A bare `map[key]`
 * resolves `__proto__`, `constructor`, `valueOf`, `toString`… to inherited
 * members, and a model-written link can carry any of them as a param name.
 */
function ownValue<T>(map: Readonly<Record<string, T>>, key: string): T | undefined {
  return Object.prototype.hasOwnProperty.call(map, key) ? map[key] : undefined
}

// Ids, codes and search text are short and plain. Anything outside these is
// refused at build time and at render time alike.
const ID_RE = /^[A-Za-z0-9_-]{1,80}$/
const SEARCH_RE = /^[A-Za-z0-9 ._@+-]{1,80}$/
const ZONE_TABS = new Set(['rates', 'events', 'hours', 'closures'])

/** Path → the query params it may carry, each with its own value rule. */
const ALLOWED: Record<string, Record<string, (v: string) => boolean>> = {
  '/zones': { zone: v => ID_RE.test(v), tab: v => ZONE_TABS.has(v) },
  '/avery': { proposal: v => ID_RE.test(v) },
  '/transactions': { q: v => SEARCH_RE.test(v) },
  '/permits': { permit: v => ID_RE.test(v) },
  '/validations': { edit: v => ID_RE.test(v) },
}

/** Params each path must carry: a link to a bare screen is no deep link. */
const REQUIRED: Record<string, string[]> = {
  '/zones': ['zone'],
  '/avery': ['proposal'],
  '/transactions': ['q'],
  '/permits': ['permit'],
  '/validations': ['edit'],
}

const ZONE_TAB_FOR: Partial<Record<ConsoleLinkTarget, string>> = {
  zone_rates: 'rates',
  zone_events: 'events',
  zone_hours: 'hours',
  zone_closures: 'closures',
}

/**
 * Build the relative console path for a target. `ref` is the resolved record
 * reference (a zone's canonical id, a proposal id, a permit id, a validation
 * code, or search text for transactions). Returns null when `ref` fails its
 * value rule, so the caller reports an error instead of handing out a guess.
 */
export function buildConsoleLinkPath(target: ConsoleLinkTarget, ref: string): string | null {
  const params = new URLSearchParams()
  let path: string
  switch (target) {
    case 'zone':
    case 'zone_rates':
    case 'zone_events':
    case 'zone_hours':
    case 'zone_closures': {
      path = '/zones'
      params.set('zone', ref)
      const tab = ZONE_TAB_FOR[target]
      if (tab) params.set('tab', tab)
      break
    }
    case 'proposal': path = '/avery'; params.set('proposal', ref); break
    case 'transactions': path = '/transactions'; params.set('q', ref); break
    case 'permit': path = '/permits'; params.set('permit', ref); break
    case 'validation': path = '/validations'; params.set('edit', ref); break
  }
  const built = `${path}?${params.toString()}`
  return isConsoleLinkPath(built) ? built : null
}

/**
 * True only for a relative console path listed above, carrying only its
 * allowed params with valid values. The render-side gate: Avery's text is
 * model output, so every link in it is re-checked here, never trusted.
 */
export function isConsoleLinkPath(raw: string): boolean {
  if (typeof raw !== 'string' || raw.length > 300) return false
  // Relative, single leading slash: rejects `//evil.com`, `https:`, `javascript:`.
  if (!raw.startsWith('/') || raw.startsWith('//') || raw.includes('\\')) return false
  let url: URL
  try {
    url = new URL(raw, 'https://console.invalid')
  } catch {
    return false
  }
  if (url.origin !== 'https://console.invalid' || url.hash) return false
  const allowed = ownValue(ALLOWED, url.pathname)
  if (!allowed) return false
  const seen = new Set<string>()
  for (const [k, v] of url.searchParams) {
    const rule = ownValue(allowed, k)
    if (!rule || seen.has(k) || !rule(v)) return false
    seen.add(k)
  }
  return (ownValue(REQUIRED, url.pathname) ?? []).every(k => seen.has(k))
}
